import {styled} from '@mui/material/styles';

import {
  ActiveFiltersSectionConfig,
  FilterDialogSection,
  ActiveFilterSectionContent,
} from 'filters/features/FilterDialog/types';

import {useFilterDialogContext} from 'filters/features/FilterDialog/contexts/FilterDialogProvider/FilterDialogProvider';
import {TreeViewActiveFilters} from 'filters/features/FilterDialog/sections/ActiveFiltersSection/sections/TreeViewActiveFilters';
import {ItemsActiveFilters} from 'filters/features/FilterDialog/sections/ActiveFiltersSection/sections/ItemsActiveFilters';
import {neverCase} from 'utils/misc';
import {ActiveFiltersProvider} from 'filters/features/FilterDialog/sections/ActiveFiltersSection/contexts/ActiveFiltersContext';

export const createActiveFiltersSection = <PrependProps, AppendProps>(
  config: Omit<ActiveFiltersSectionConfig<PrependProps, AppendProps>, 'type'>
): ActiveFiltersSectionConfig<PrependProps, AppendProps> => {
  return {
    ...config,
    type: 'active-filters' as const,
  };
};

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(8),
  padding: theme.spacing(6),
  containerType: 'inline-size',
}));

const SectionRenderer = ({section}: {section: FilterDialogSection}) => {
  switch (section.type) {
    case 'active-filters':
    case 'recent-filters':
    case 'saved-filters':
      return null;
    case 'tree-view':
      return <TreeViewActiveFilters {...section} />;
    case 'items':
      return <ItemsActiveFilters {...section} />;
    default:
      return neverCase(section);
  }
};

export const ActiveFiltersSection = ({
  className,
  prependContent: PrependContent,
  prependProps,
  appendContent: AppendContent,
  appendProps,
  overrideContent,
}: ActiveFiltersSectionConfig & {
  className?: string;
  overrideContent?: (content: ActiveFilterSectionContent) => ActiveFilterSectionContent;
}) => {
  const {sections} = useFilterDialogContext();

  return (
    <ActiveFiltersProvider overrideContent={overrideContent}>
      <SRoot className={className}>
        {PrependContent && <PrependContent {...prependProps} />}
        {sections.map(section => (
          <SectionRenderer key={section.id} section={section} />
        ))}
        {AppendContent && <AppendContent {...appendProps} />}
      </SRoot>
    </ActiveFiltersProvider>
  );
};
